import { useState, useEffect } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { checkPinValidity } from '../utils/pinAuth';
import { usePinInput, PinInputGroup } from './PinInput';

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const shake = keyframes`
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-6px); }
  75% { transform: translateX(6px); }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: var(--card-bg);
  border-radius: 8px;
  width: 90%;
  max-width: 480px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  ${({ $shake }) => $shake && css`animation: ${shake} 0.4s ease;`}
`;

const ModalHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid var(--border-color);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 1.25rem;
  color: var(--text-color);
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-secondary);
  font-size: 1.25rem;
  line-height: 1;
  padding: 0;

  &:hover {
    color: var(--text-color);
  }
`;

const ModalBody = styled.div`
  padding: 1.5rem;
`;

const ModalFooter = styled.div`
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--border-color);
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
`;

const Btn = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: var(--border-radius);
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  border: none;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const PrimaryBtn = styled(Btn)`
  background: var(--primary-color);
  color: white;

  &:hover:not(:disabled) {
    background: var(--primary-dark);
  }
`;

const SecondaryBtn = styled(Btn)`
  background: var(--bg-secondary);
  color: var(--text-primary);
  border: 1px solid var(--border-color);

  &:hover:not(:disabled) {
    background: var(--bg-hover);
  }
`;

const SpinIcon = styled.svg`
  animation: ${spin} 1s linear infinite;
  flex-shrink: 0;
`;

const ProviderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.3rem 0.5rem;
  border-radius: 5px;
  font-size: 0.875rem;
  color: var(--text-color);
`;

const AddedRow = styled(ProviderRow)`
  background: rgba(52, 211, 153, 0.08);
  border: 1px solid rgba(52, 211, 153, 0.2);
`;

const RemovedRow = styled(ProviderRow)`
  background: rgba(248, 113, 113, 0.08);
  border: 1px solid rgba(248, 113, 113, 0.2);
`;

const RowIdx = styled.span`
  min-width: 1.25rem;
  font-weight: 600;
  color: var(--text-secondary);
  font-size: 0.75rem;
`;

const SectionLabel = styled.div`
  font-weight: 600;
  margin-bottom: 0.375rem;
  font-size: 0.875rem;
`;

export default function SyncProvidersModal({ syncModal, onConfirm, onClose }) {
  const [pinVisible, setPinVisible] = useState(false);
  const {
    pin, pinError, shake: doShake, attempts, locked,
    inputRefs, handleChange, handleKeyDown, submit, reset,
  } = usePinInput({ onSuccess: () => { setPinVisible(false); onConfirm(); } });

  useEffect(() => {
    if (!syncModal) {
      setPinVisible(false);
      reset();
    }
  }, [syncModal]);

  if (!syncModal) return null;

  const { pharmacyName, phase, added, removed, error } = syncModal;

  const handleSyncNow = () => {
    if (checkPinValidity()) { onConfirm(); return; }
    setPinVisible(true);
  };

  const titles = { confirm: 'Sync Providers', syncing: 'Syncing Providers...', summary: 'Sync Complete' };

  return (
    <ModalOverlay>
      <ModalContent $shake={doShake}>
        <ModalHeader>
          <ModalTitle>{pinVisible ? 'Enter PIN to Sync' : titles[phase]}</ModalTitle>
          {phase !== 'syncing' && <CloseButton onClick={onClose}>×</CloseButton>}
        </ModalHeader>

        <ModalBody>
          {pinVisible ? (
            <div style={{ textAlign: 'center' }}>
              <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                Enter the 4-digit PIN to sync <strong>{pharmacyName}</strong>.
              </p>
              {locked ? (
                <p style={{ color: '#ef4444', marginTop: '1rem' }}>
                  Access locked. Please <strong>logout and login again</strong> to unlock.
                </p>
              ) : (
                <PinInputGroup
                  pin={pin}
                  onChange={handleChange}
                  onKeyDown={handleKeyDown}
                  inputRefs={inputRefs}
                  pinError={pinError}
                  attempts={attempts}
                />
              )}
            </div>
          ) : (
            <>
              {phase === 'confirm' && (
                <div>
                  <p style={{ margin: '0 0 0.75rem', color: 'var(--text-color)' }}>
                    Sync providers for <strong>{pharmacyName}</strong>?
                  </p>
                  <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                    This will update the provider list for this pharmacy only. Providers may be added or
                    removed. Existing orders and their assigned providers will not be affected.
                  </p>
                </div>
              )}

              {phase === 'syncing' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', color: 'var(--text-secondary)' }}>
                  <SpinIcon width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="23 4 23 10 17 10" />
                    <polyline points="1 20 1 14 7 14" />
                    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                  </SpinIcon>
                  <span>Syncing {pharmacyName}... please wait</span>
                </div>
              )}

              {phase === 'summary' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {error ? (
                    <p style={{ color: 'var(--error-color)', margin: 0 }}>Sync failed. Please try again.</p>
                  ) : added.length === 0 && removed.length === 0 ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                      <span>✓</span>
                      <span>No changes detected. Provider list is already up to date.</span>
                    </div>
                  ) : (
                    <>
                      {added.length > 0 && (
                        <div>
                          <SectionLabel style={{ color: '#34d399' }}>
                            ✓ {added.length} provider{added.length > 1 ? 's' : ''} added
                          </SectionLabel>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                            {added.map((p, i) => (
                              <AddedRow key={p.id}>
                                <RowIdx>{i + 1}.</RowIdx>
                                <span>{p.name}</span>
                              </AddedRow>
                            ))}
                          </div>
                        </div>
                      )}
                      {removed.length > 0 && (
                        <div>
                          <SectionLabel style={{ color: '#f87171' }}>
                            ✕ {removed.length} provider{removed.length > 1 ? 's' : ''} removed from list
                          </SectionLabel>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                            {removed.map((p, i) => (
                              <RemovedRow key={p.id}>
                                <RowIdx>{i + 1}.</RowIdx>
                                <span>{p.name}</span>
                              </RemovedRow>
                            ))}
                          </div>
                          <p style={{ margin: '0.5rem 0 0', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                            Existing orders with these providers are not affected.
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </ModalBody>

        <ModalFooter>
          {pinVisible && !locked && (
            <>
              <SecondaryBtn onClick={onClose}>Cancel</SecondaryBtn>
              <PrimaryBtn onClick={submit} disabled={pin.join('').length < 4}>Confirm</PrimaryBtn>
            </>
          )}
          {pinVisible && locked && (
            <SecondaryBtn onClick={onClose}>Close</SecondaryBtn>
          )}
          {!pinVisible && phase === 'confirm' && (
            <>
              <SecondaryBtn onClick={onClose}>Cancel</SecondaryBtn>
              <PrimaryBtn onClick={handleSyncNow}>Sync Now</PrimaryBtn>
            </>
          )}
          {!pinVisible && phase === 'summary' && (
            <PrimaryBtn onClick={onClose}>Close</PrimaryBtn>
          )}
        </ModalFooter>
      </ModalContent>
    </ModalOverlay>
  );
}
